import {
  useQuery,
  useMutation,
  useQueryClient,
  UseQueryOptions,
} from 'react-query';
import { COOKIES_CONFIG } from '@belong/common';
import { parseCookies } from 'nookies';
import {
  BlobUploadResponse,
  UserBasicInfoModel,
  UserModel,
} from 'src/api/models';
import {
  fetchUserClaimsService,
  fetchUserProfileService,
  fetchUserService,
  updateUserProfileService,
  deleteUserProfileImageService,
  getUserProfileImageUploadUrlService,
  createUserProfileImageService,
  sendTextMessageService,
} from 'src/api/users';
import { getJwtToken } from 'src/utils/api-client';

const { JWT_TOKEN } = COOKIES_CONFIG;

export type ContactInfo = {
  email: string;
  phone: string;
};

export type UserClaimModel = {
  isHomeOwner: boolean;
  isResident: boolean;
};

export function useUser() {
  const cookies = parseCookies();
  const jwtToken = cookies[JWT_TOKEN.name] || getJwtToken();
  const { data, isError, isLoading, refetch } = useQuery<UserModel>(
    'user',
    () => fetchUserService(),
    {
      retry: 0,
      // should be refetched in the background every 24 hours
      staleTime: 1000 * 60 * 60 * 24,
      enabled: !!jwtToken,
    },
  );

  return {
    user: data,
    isUserError: isError,
    isLoadingUser: isLoading,
    refetchUser: refetch,
  };
}

export function useMutateUser() {
  const queryClient = useQueryClient();
  return useMutation(() => fetchUserService(), {
    onSuccess: (data) => {
      queryClient.setQueryData('user', data);
    },
  });
}

export function useUserProfile(options?: UseQueryOptions<UserBasicInfoModel>) {
  return useQuery<UserBasicInfoModel>(
    'users/profile',
    () => fetchUserProfileService(),
    options,
  );
}

export function useUpdateUserProfile() {
  const queryClient = useQueryClient();

  return useMutation(updateUserProfileService, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      queryClient.invalidateQueries('users/profile');
    },
  });
}

export function useUserClaims(options?: UseQueryOptions<UserClaimModel>) {
  const { data, refetch } = useQuery<UserClaimModel>(
    'users/claims',
    () => fetchUserClaimsService(),
    options,
  );

  return {
    userClaims: data,
    refetchUserClaims: refetch,
  };
}

export function useGetUserProfileImageUploadUrl() {
  return useQuery<BlobUploadResponse>(
    'users/profile-image/upload-url',
    () => getUserProfileImageUploadUrlService(),
    {
      enabled: false,
    },
  );
}

export function useDeleteUserProfileImage() {
  const queryClient = useQueryClient();

  return useMutation(deleteUserProfileImageService, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      queryClient.invalidateQueries('users/profile');
    },
  });
}

export function useCreateUserProfileImage() {
  const queryClient = useQueryClient();

  return useMutation(createUserProfileImageService, {
    onSuccess: () => {
      queryClient.invalidateQueries('users');
      queryClient.invalidateQueries('users/profile');
    },
  });
}

export function useSendTextMessage() {
  return useMutation(sendTextMessageService);
}
